import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Layout/Sidebar';
import { api } from '../api';
import { Copy, Trash2, UserPlus, Loader2, X } from 'lucide-react';

export default function Team() {
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'agent' });
  const [inviteLink, setInviteLink] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersData, invitesData] = await Promise.all([
        api('/api/users'),
        api('/api/invites')
      ]);
      setUsers(usersData);
      setInvites(invitesData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await api(`/api/users/${userId}/role`, {
        method: 'PATCH',
        body: JSON.stringify({ role: newRole })
      });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
    } catch (err) {
      console.error(err);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviteLoading(true);
    setError(null);
    try {
      const data = await api('/api/invites/send', {
        method: 'POST',
        body: JSON.stringify(inviteForm)
      });
      setInviteLink(data.inviteLink);
      loadData();
    } catch (err) {
      setError(err.message || 'Failed to create invite');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCopy = (link) => {
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRevokeInvite = async (id) => {
    try {
      await api(`/api/invites/${id}`, { method: 'DELETE' });
      setInvites(prev => prev.filter(i => i.id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  const roleColors = {
    admin: 'bg-purple-100 text-purple-700',
    producer: 'bg-blue-100 text-blue-700',
    account_executive: 'bg-green-100 text-green-700',
    agent: 'bg-slate-100 text-slate-700'
  };

  return (
    <div className="flex h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white border-b px-8 py-4 flex justify-between items-center">
          <h1 className="text-xl font-bold text-slate-800">Team Management</h1>
        </header>

        <main className="flex-1 overflow-y-auto p-8 space-y-8">
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-slate-700">Team Members</h2>
              <button
                onClick={() => { setShowModal(true); setInviteLink(null); setError(null); }}
                className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 transition"
              >
                <UserPlus size={16} />
                Invite User
              </button>
            </div>
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              {loading ? (
                <div className="p-6 text-center text-slate-500">Loading...</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left px-6 py-3 font-semibold text-slate-600">Name</th>
                      <th className="text-left px-6 py-3 font-semibold text-slate-600">Email</th>
                      <th className="text-left px-6 py-3 font-semibold text-slate-600">Role</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(user => (
                      <tr key={user.id} className="border-b last:border-0 hover:bg-slate-50">
                        <td className="px-6 py-4 font-medium text-slate-800">
                          {user.first_name} {user.last_name}
                        </td>
                        <td className="px-6 py-4 text-slate-600">{user.email}</td>
                        <td className="px-6 py-4">
                          <select
                            value={user.role}
                            onChange={(e) => handleRoleChange(user.id, e.target.value)}
                            className={`text-xs font-semibold px-2 py-1 rounded-full border-0 outline-none cursor-pointer ${roleColors[user.role] || roleColors.agent}`}
                          >
                            <option value="admin">Admin</option>
                            <option value="producer">Producer</option>
                            <option value="account_executive">Account Executive</option>
                            <option value="agent">Agent</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {invites.filter(i => !i.used).length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-slate-700 mb-4">Pending Invites</h2>
              <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left px-6 py-3 font-semibold text-slate-600">Email</th>
                      <th className="text-left px-6 py-3 font-semibold text-slate-600">Role</th>
                      <th className="text-left px-6 py-3 font-semibold text-slate-600">Invite Link</th>
                      <th className="text-left px-6 py-3 font-semibold text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invites.filter(i => !i.used).map(invite => (
                      <tr key={invite.id} className="border-b last:border-0 hover:bg-slate-50">
                        <td className="px-6 py-4 text-slate-800">{invite.email}</td>
                        <td className="px-6 py-4">
                          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${roleColors[invite.role] || roleColors.agent}`}>
                            {invite.role}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => handleCopy(`http://prorately.com/#/register?invite=${invite.token}`)}
                            className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-xs"
                          >
                            <Copy size={14} /> Copy Link
                          </button>
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => handleRevokeInvite(invite.id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </main>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4">
            <div className="flex justify-between items-center px-6 py-4 border-b">
              <h2 className="text-lg font-semibold text-slate-800">Invite Team Member</h2>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600">
                <X size={20} />
              </button>
            </div>

            <div className="p-6">
              {!inviteLink ? (
                <form onSubmit={handleInvite} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Email Address</label>
                    <input
                      type="email"
                      value={inviteForm.email}
                      onChange={(e) => setInviteForm(prev => ({ ...prev, email: e.target.value }))}
                      required
                      placeholder="colleague@agency.com"
                      className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
                    <select
                      value={inviteForm.role}
                      onChange={(e) => setInviteForm(prev => ({ ...prev, role: e.target.value }))}
                      className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    >
                      <option value="producer">Producer</option>
                      <option value="account_executive">Account Executive</option>
                      <option value="agent">Agent</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>

                  {error && (
                    <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                      {error}
                    </div>
                  )}

                  <div className="flex justify-end gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="px-4 py-2 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-100 transition"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={inviteLoading}
                      className="px-4 py-2 rounded-xl text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 transition disabled:opacity-50"
                    >
                      <span className="inline-flex items-center gap-2">
                        {inviteLoading && <Loader2 className="animate-spin" size={16} />}
                        {inviteLoading ? 'Generating...' : 'Generate Invite Link'}
                      </span>
                    </button>
                  </div>
                </form>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-slate-600">Share this link with your team member:</p>
                  <div className="bg-slate-50 rounded-xl p-3 text-xs text-slate-700 break-all border">
                    {inviteLink}
                  </div>
                  <button
                    onClick={() => handleCopy(inviteLink)}
                    className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-blue-700 transition"
                  >
                    <Copy size={16} />
                    {copied ? 'Copied!' : 'Copy Link'}
                  </button>
                  <button
                    onClick={() => { setShowModal(false); setInviteLink(null); setInviteForm({ email: '', role: 'agent' }); }}
                    className="w-full px-4 py-2 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-100 transition"
                  >
                    Done
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}